/**
 * rekey.ts — Web key-store wipe detection + constrained auto re-key.
 *
 * The E2EE device private keys live in IndexedDB (`kollegeplus-e2ee` →
 * `deviceKeys`) with a localStorage mirror (`kollegeplus:deviceKeys`). A *wipe*
 * is when BOTH are gone but the user has used this browser before. Today that
 * looks identical to a brand-new device: the init path silently registers fresh
 * and never proactively asks peers to redistribute their group sender keys, so
 * historical group messages stay undecryptable and recovery is purely reactive.
 *
 * We distinguish wipe-from-new with a DURABLE marker (`lastKnownDeviceId`) kept
 * under its OWN localStorage key — deliberately NOT inside the IDB store or the
 * deviceKeys mirror, since those are exactly what gets wiped. A full
 * browser-data clear also drops the marker → we correctly fall back to the plain
 * new-device flow (a wipe is genuinely indistinguishable from a new device once
 * every trace is gone).
 *
 * ── Cascade-reactivation guard (merged 2782cd5) ──────────────────────────────
 * The client deliberately does NOT auto-re-register when the server deactivated
 * a device for `reason=cascade` (see device-heal.ts), and the server refuses
 * reactivation during the cascade cooldown (`reactivationBlocked`). Auto re-key
 * must NOT fight this:
 *   - Re-key ALWAYS mints a NEW deviceId via the existing registration path. It
 *     NEVER attempts to reactivate `lastKnownDeviceId`, so the per-old-deviceId
 *     backoff and the server's reactivation refusal don't apply to the fresh id.
 *   - If a cascade-block is locally persisted for the prior device, we skip
 *     registration entirely and surface the existing blocked UX (`onBlocked`).
 *   - If the server still rejects the fresh registration with
 *     `reactivation_blocked:*`, we give up for the session (no retry loop),
 *     log, and surface the blocked UX.
 *
 * Everything is dependency-injected so the policy is unit-testable without a
 * real browser, IndexedDB, or the websocket.
 */

// ── Durable lastKnownDeviceId marker ─────────────────────────────────────────

/** Own localStorage key — NOT the deviceKeys mirror, NOT in IDB (both wipe-able). */
export const LAST_KNOWN_DEVICE_ID_KEY = "kollegeplus:lastKnownDeviceId";

/** The per-old-deviceId re-register backoff key written by device-heal.ts. Its
 *  presence within the backoff window is the only client-persistent signal that
 *  a (potentially cascade-)deactivation just happened for that device. */
const REREGISTER_AT_PREFIX = "kp_reregister_at:";
/** Mirror device-heal's REREGISTER_BACKOFF_MS so we read the same window. */
const REREGISTER_BACKOFF_MS = 5 * 60 * 1000;

export function saveLastKnownDeviceId(deviceId: string): void {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(LAST_KNOWN_DEVICE_ID_KEY, deviceId);
    }
  } catch {
    /* quota / private mode — non-critical, just disables wipe detection */
  }
}

export function readLastKnownDeviceId(): string | null {
  try {
    return typeof localStorage !== "undefined"
      ? localStorage.getItem(LAST_KNOWN_DEVICE_ID_KEY)
      : null;
  } catch {
    return null;
  }
}

export function clearLastKnownDeviceId(): void {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem(LAST_KNOWN_DEVICE_ID_KEY);
    }
  } catch {
    /* non-critical */
  }
}

/**
 * Is the prior device under an active re-register backoff? device-heal.ts writes
 * `kp_reregister_at:<deviceId>` as an ORDINARY-deactivation re-register debounce
 * (timestamp set before each re-register attempt so we don't hammer the server).
 * It is NOT the cascade cooldown — the cascade path in device-heal.ts never
 * writes this key. Cascade safety comes from a different mechanism: re-key always
 * mints a FRESH deviceId, and the SERVER enforces a per-deviceId reactivation
 * guard that rejects reactivating a cascade-deactivated device. We still treat a
 * fresh debounce marker as "blocked" here, because its presence means a
 * deactivation just happened for the prior device and we'd rather surface the
 * clear-data UX than auto-re-key in the middle of an in-flight recovery.
 */
export function isPriorDeviceReregisterBlocked(
  lastKnownDeviceId: string | null,
  now: number = Date.now(),
): boolean {
  if (!lastKnownDeviceId) return false;
  try {
    if (typeof localStorage === "undefined") return false;
    const raw = localStorage.getItem(REREGISTER_AT_PREFIX + lastKnownDeviceId);
    if (!raw) return false;
    const at = parseInt(raw, 10);
    if (Number.isNaN(at)) return false;
    return now - at < REREGISTER_BACKOFF_MS;
  } catch {
    return false;
  }
}

// ── Wipe detection ───────────────────────────────────────────────────────────

export interface WipeState {
  /** Did init recover device private keys (from IDB or the localStorage mirror)? */
  hasPrivateKeys: boolean;
  /** The durable marker from a previous run, or null if this browser is fresh. */
  lastKnownDeviceId: string | null;
}

/**
 * A wipe = we have NO private keys but DO remember a prior deviceId. Missing
 * keys with no prior id is a plain new device (indistinguishable, by design).
 */
export function detectWipe(state: WipeState): boolean {
  return !state.hasPrivateKeys && !!state.lastKnownDeviceId;
}

// ── Constrained auto re-key ──────────────────────────────────────────────────

export interface RekeyEvent {
  phase: "e2ee_store_wipe_detected";
  /** The prior deviceId that was wiped. */
  priorDeviceId: string | null;
  /** The freshly registered deviceId, if registration succeeded. */
  newDeviceId?: string | null;
  /** "blocked" | "registered" | "register_failed" */
  outcome: "blocked" | "registered" | "register_failed";
  error?: string;
}

export interface RekeyDeps {
  /** Register a brand-new device (mints + persists its OWN fresh deviceId).
   *  Resolves to the new deviceId. Rejects with `reactivation_blocked:*` if the
   *  server refuses during cascade cooldown. */
  registerNewDevice: () => Promise<string>;
  /** Cascade-guard hook: true → a deactivation/cooldown is in effect locally,
   *  skip registration. Defaults to the backoff-marker check. */
  isCascadeBlocked?: (lastKnownDeviceId: string | null) => boolean;
  /** Surface the existing "blocked — clear data and re-login" UX. */
  onBlocked?: (reason: string) => void;
  /** Structured telemetry sink. */
  emit?: (ev: RekeyEvent) => void;
  /** Logger. */
  log?: { info: (...a: unknown[]) => void; warn: (...a: unknown[]) => void };
}

const noopLog = { info: () => {}, warn: () => {} };

/**
 * If `state` indicates a wipe, perform a constrained auto re-key:
 *  1. honor the cascade guard (skip + surface blocked UX if active);
 *  2. register a NEW device (never reactivate the wiped one);
 *  3. on success, refresh the durable marker to the new deviceId;
 *  4. on `reactivation_blocked` / generic failure, give up for the session
 *     (no retry loop) and surface the blocked UX.
 *
 * Redistribution of group sender keys to the fresh device is intentionally NOT
 * handled here. A wiped device has zero sender keys (own + peer) in every group
 * channel, so F23b's WS-connect handler (useChat.ts:2108,
 * `request_channel_redistribute`) fires for ALL of them on connect — with its
 * own session-scoped dedupe and `redistribute_request_sent` telemetry, and
 * re-firing on every reconnect. Duplicating that here would only add an
 * un-deduped, WS-OPEN-racing second path, so we delegate to the F23b rail.
 *
 * Returns the new deviceId on a successful re-key, else null. A null return on a
 * detected wipe means the caller should fall through to its normal new-device
 * registration (which already mints + registers a fresh id).
 */
export async function maybeRekey(
  state: WipeState,
  deps: RekeyDeps,
): Promise<string | null> {
  if (!detectWipe(state)) return null;

  const log = deps.log ?? noopLog;
  const emit = deps.emit ?? (() => {});
  const isBlocked =
    deps.isCascadeBlocked ?? ((id) => isPriorDeviceReregisterBlocked(id));
  const priorDeviceId = state.lastKnownDeviceId;

  log.info("[rekey] key-store wipe detected", { priorDeviceId });

  if (isBlocked(priorDeviceId)) {
    log.warn("[rekey] cascade-blocked — refusing auto re-key", { priorDeviceId });
    emit({ phase: "e2ee_store_wipe_detected", priorDeviceId, outcome: "blocked" });
    deps.onBlocked?.("cascade_block_local");
    return null;
  }

  let newDeviceId: string;
  try {
    newDeviceId = await deps.registerNewDevice();
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    // No retry loop: a reactivation_blocked rejection means the server is in
    // cascade cooldown; a generic failure means we let the normal init path /
    // next boot try again. Either way we stop here for this attempt.
    if (msg.startsWith("reactivation_blocked")) {
      log.warn("[rekey] server refused registration (reactivation_blocked) — giving up for session", msg);
      deps.onBlocked?.(msg);
    } else {
      log.warn("[rekey] new-device registration failed — giving up for session", msg);
    }
    emit({
      phase: "e2ee_store_wipe_detected",
      priorDeviceId,
      outcome: "register_failed",
      error: msg,
    });
    return null;
  }

  // Registration succeeded on a fresh id — the wiped device is gone for good.
  clearLastKnownDeviceId();
  saveLastKnownDeviceId(newDeviceId);

  emit({
    phase: "e2ee_store_wipe_detected",
    priorDeviceId,
    newDeviceId,
    outcome: "registered",
  });
  log.info("[rekey] re-keyed onto fresh device", { priorDeviceId, newDeviceId });

  // Redistribution is delegated to F23b (useChat.ts:2108): on WS-connect it
  // detects the keyless group channels (which a wipe produces for ALL of them)
  // and emits request_channel_redistribute with dedupe + telemetry. No trigger
  // is needed here.

  return newDeviceId;
}
